import React, { useCallback, useContext, Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import { ALBUM_PHOTO_FILTERS } from '../../../../../../../../../constants/albums';
import { BASENAME } from '../../../../../../../../../constants/basenames';
import Button from '../../../../../../../../../components/global/Button';
import useBasename from '../../../../../../../../../components/routing/useBasename';
import { albumPhotosFilterUpdate } from '../../../../../../../redux/albumPhotosFilter/albumPhotosFilterActions';
import PhotoGroupHeaderFilterItem from '../../../../../AlbumHeader/components/PhotoGroupHeaderFilterItem/PhotoGroupHeaderFilterItem';
import CustomControl from '../../../../../../../../../components/global/CustomControl';
import { PhotoGroupContext } from '../../../../../helpers/PhotoGroupContext';
import {
  isYearbookEntity,
  shouldAccessScheduledUpload,
} from '../../../../../../../../../util/product-permissions';
import { PhotoSelectorContext } from '../../../../../../../../../components/global/PhotoSelectorContext';
import InfoCircleIcon from '../../../../../../../../../components/icons/InfoCircleIcon';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  totalPhotos: PropTypes.number,
  useFauxTaggingMode: PropTypes.bool,
  setUseFauxTaggingMode: PropTypes.func,
};

const AlbumManualMatchingHeader = ({
  album,
  totalPhotos,
  useFauxTaggingMode,
  setUseFauxTaggingMode,
}) => {
  const {
    selectedPhotos,
    selectionEnabled,
    onResetSelection,
    onToggleSelectionEnabled,
  } = useContext(PhotoSelectorContext);
  const basename = useBasename();
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectionSettings, setSelectionSettings } =
    useContext(PhotoGroupContext);
  const { showFileNames } = selectionSettings;
  const onAlbumPhotosFilterUpdate = useCallback(
    (...args) => dispatch(albumPhotosFilterUpdate(...args)),
    [dispatch],
  );

  const onSelectPhotosFilter = filter => {
    onAlbumPhotosFilterUpdate(filter);
    history.push(`/app/${basename}/albums/${album?.id}/photos`);
  };

  const onShowFilenamesToggle = () => {
    setSelectionSettings({
      ...selectionSettings,
      showFileNames: !showFileNames,
    });
  };

  const showSlideshowAction =
    basename !== BASENAME.SHUTTERBUG && !album?.picture_day_enabled;
  const showConsumerMatchesAction =
    isYearbookEntity(album) && album?.members_with_matched_photos?.total > 0;
  const showScheduleAction =
    shouldAccessScheduledUpload(basename) &&
    (album?.photo_schedules?.total ?? 0) > 0;

  return (
    <div className="photo-group-header mb-2">
      <div className="photo-group-header-filter-actions">
        <PhotoGroupHeaderFilterItem
          onAlbumPhotoFilterUpdate={onSelectPhotosFilter}
          value={ALBUM_PHOTO_FILTERS.ALL}
        />
        <PhotoGroupHeaderFilterItem
          onAlbumPhotoFilterUpdate={onSelectPhotosFilter}
          value={ALBUM_PHOTO_FILTERS.PUBLISHED}
        />
        {/* Show the Hidden Photos section for backward compatibility */}
        {basename !== BASENAME.SHUTTERBUG && (
          <PhotoGroupHeaderFilterItem
            onAlbumPhotoFilterUpdate={onSelectPhotosFilter}
            value={ALBUM_PHOTO_FILTERS.HIDDEN}
          />
        )}
        <PhotoGroupHeaderFilterItem
          onAlbumPhotoFilterUpdate={onSelectPhotosFilter}
          value={ALBUM_PHOTO_FILTERS.REVIEWING}
          className={album?.photos_for_review?.total > 0 ? 'text-danger' : ''}
        />
        {showConsumerMatchesAction && (
          <Button
            btnStyle="reset"
            className="photo-group-header-filter-action-item"
            onClick={() =>
              history.replace(
                `/app/${basename}/albums/${album?.id}/consumer-matches`,
              )
            }
          >
            Yearbook
          </Button>
        )}
        {showSlideshowAction && (
          <Button
            btnStyle="reset"
            className="photo-group-header-filter-action-item"
            onClick={() =>
              history.replace(
                `/app/${basename}/albums/${album?.id}/slideshow-curation`,
              )
            }
          >
            Slideshow
          </Button>
        )}
        <Button
          btnStyle="reset"
          className="photo-group-header-filter-action-item selected"
          onClick={() =>
            history.replace(
              `/app/${basename}/albums/${album?.id}/manual-matching`,
            )
          }
        >
          Unmatched
        </Button>
        {showScheduleAction && (
          <Button
            btnStyle="reset"
            className="photo-group-header-filter-action-item"
            onClick={() =>
              history.replace(`/app/${basename}/albums/${album?.id}/schedule`)
            }
          >
            Schedule
          </Button>
        )}
      </div>
      <div className="photo-group-header-subactions ml-md-auto">
        <div className="d-flex flex-column mb-md-0 mb-2">
          <div className="d-flex flex-row align-items-center">
            <CustomControl
              id="checkbox-show-file-names"
              label="Display filenames"
              type="checkbox"
              checked={showFileNames}
              readOnly
              size="sm"
              className="photo-group-checkbox-filenames text-extra-bold ml-auto d-md-inline-block d-none"
              onClick={onShowFilenamesToggle}
            />
            {setUseFauxTaggingMode && (
              <CustomControl
                id="checkbox-faux-tagging-mode"
                label={
                  <Fragment>
                    Use Faux Tagging{' '}
                    <span
                      className="v-align-sub"
                      data-for="tooltip-faux-tagging"
                      data-tip=""
                      data-place="top"
                      data-effect="solid"
                    >
                      <InfoCircleIcon color="#0d84fe" size={16} />
                    </span>
                    <ReactTooltip
                      id="tooltip-faux-tagging"
                      className="tooltip tooltip-faux-tagging"
                    >
                      <Fragment>
                        Enable this to create face tags on photos without
                        detected faces.
                        <br />
                        Tags will be placed at the bottom-left corner of the
                        photo.
                      </Fragment>
                    </ReactTooltip>
                  </Fragment>
                }
                type="checkbox"
                checked={useFauxTaggingMode}
                disabled={selectionEnabled}
                readOnly
                size="sm"
                className="photo-group-checkbox-filenames text-extra-bold ml-2 d-md-inline-block d-none"
                onClick={() => setUseFauxTaggingMode(!useFauxTaggingMode)}
              />
            )}
            {totalPhotos > 0 &&
              (selectedPhotos ?? [])?.length === 0 &&
              !selectionEnabled && (
                <Button
                  btnStyle="link"
                  className="d-md-inline-flex ml-md-2 ml-auto"
                  btnSize="sm"
                  onClick={() => onToggleSelectionEnabled()}
                >
                  Select Photos
                </Button>
              )}
            {((selectedPhotos ?? [])?.length > 0 || selectionEnabled) && (
              <Button
                btnStyle="link"
                className="d-md-inline-flex ml-md-2 ml-auto text-danger"
                btnSize="sm"
                onClick={() => onResetSelection()}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

AlbumManualMatchingHeader.propTypes = propTypes;

export default AlbumManualMatchingHeader;
